import { PROFILE_COMPLETION_SCORING } from "../config/profileCompletionScoring";
import constants from "../Utils/constants";
import docsData from "../Utils/docsData";
import { getKycDocumentStatus, getKycResolutionStatus } from "./common";

const clamp = (value, minimum, maximum) =>
  Math.min(maximum, Math.max(minimum, value));

const toNonNegativeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : null;
};

const getCollectionCount = (value) => {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === "object") return Object.keys(value).length;
  return null;
};

// ID document result; AML is added on the landlord side (isAmlUnderReview).
export const getKycStatus = (user) =>
  String(getKycDocumentStatus(user) ?? "not_started").toLowerCase();

// AML result is either "clear" or "consider"; "consider" counts as review
// unless a landlord has manually resolved every non-clear AML category.
const isAmlUnderReview = (user, resolutions) => {
  const kycUser = user?.onfido_user_kyc;
  const amlResult = kycUser?.kyc_aml?.result;
  if (!amlResult || amlResult === "clear") return false;
  if (!resolutions) return true;

  const { categoryResults, resolvedCategoryKeys } = getKycResolutionStatus(
    kycUser,
    resolutions
  );
  const unresolvedAml = categoryResults.nonClear.filter(
    (category) =>
      category.section === "aml_report" &&
      !resolvedCategoryKeys.has(category.key)
  );
  return unresolvedAml.length > 0;
};

export const getKycScoreState = (
  user,
  resolutions = null,
  considerKYCAML = true
) => {
  const status = getKycStatus(user);
  if (PROFILE_COMPLETION_SCORING.kyc.approvedStatuses.includes(status)) {
    if (considerKYCAML) {
      return isAmlUnderReview(user, resolutions) ? "review" : "approved";
    } else {
      return "approved";
    }
  }
  if (PROFILE_COMPLETION_SCORING.kyc.pendingStatuses.includes(status)) {
    return "pending";
  }
  if (PROFILE_COMPLETION_SCORING.kyc.rejectedStatuses.includes(status)) {
    return "declined";
  }
  if (PROFILE_COMPLETION_SCORING.kyc.reviewStatuses.includes(status)) {
    return "review";
  }
  return "pending";
};

// Company login tenants: one state for all KYB people (authorised
// signatories). Each person's KYC has the same shape as a tenant's, so
// getKycScoreState is reused per person. Declined wins, then review, then
// pending; approved only when every person is approved.
export const getKybPeopleState = (user) => {
  const people = user?.onfido_user_kyc?.kyb_people || [];
  if (!people.length) return "pending";
  const states = people.map((person) =>
    getKycScoreState({ onfido_user_kyc: person })
  );
  if (states.includes("declined")) return "declined";
  if (states.includes("review")) return "review";
  if (states.includes("pending")) return "pending";
  return "approved";
};

export const getKycPoints = (user, maximumPoints) => {
  // #NOTE: We are not considering KYC AML here, only to tenant
  const state = getKycScoreState(user, null, false);
  const { pendingPoints } = PROFILE_COMPLETION_SCORING.kyc;

  if (state === "approved") return maximumPoints;
  if (state === "pending") {
    return Math.min(maximumPoints, pendingPoints);
  }
  return 0;
};

export const getRequiredDocumentPoints = ({
  requiredCount,
  missingCount,
  uploadedCount,
  maximumPoints,
}) => {
  const required = toNonNegativeNumber(requiredCount);
  if (!required) return 0;

  const uploaded = toNonNegativeNumber(uploadedCount);
  const missing = toNonNegativeNumber(missingCount) ?? 0;
  const completedRequiredDocuments =
    uploaded ?? Math.max(0, required - missing);

  return (
    (clamp(completedRequiredDocuments, 0, required) / required) * maximumPoints
  );
};

const getRequiredDocumentBreakdown = ({
  requiredCount,
  missingCount,
  uploadedCount,
  missingDocs = [],
  maximumPoints,
}) => {
  const required = toNonNegativeNumber(requiredCount) ?? 0;
  const missing = clamp(toNonNegativeNumber(missingCount) ?? 0, 0, required);
  const suppliedUploaded = toNonNegativeNumber(uploadedCount);
  const uploadedRequired = clamp(
    suppliedUploaded ?? Math.max(0, required - missing),
    0,
    required
  );

  return {
    earned: getRequiredDocumentPoints({
      requiredCount: required,
      missingCount: missing,
      uploadedCount: uploadedRequired,
      maximumPoints,
    }),
    maximum: maximumPoints,
    required,
    uploadedRequired,
    missing,
    missingDocuments: Array.isArray(missingDocs) ? missingDocs : [],
  };
};

export const getDocumentCountsFromMissingResult = (
  result,
  fallbackRequiredCount = 0
) => {
  const missingDocs = Array.isArray(result?.missingDocs)
    ? result.missingDocs
    : [];
  const requiredDocs =
    result?.requiredDocs ?? result?.mandatoryDocs ?? result?.allRequiredDocs;
  const uploadedDocs =
    result?.uploadedRequiredDocs ?? result?.requiredUploadedDocs;

  const missingCount =
    toNonNegativeNumber(result?.missingCount) ?? missingDocs.length;
  const uploadedCount =
    toNonNegativeNumber(
      result?.uploadedRequiredCount ?? result?.requiredUploadedCount
    ) ?? getCollectionCount(uploadedDocs);
  const requiredCount =
    toNonNegativeNumber(
      result?.requiredCount ??
        result?.totalRequired ??
        result?.totalCount ??
        result?.mandatoryCount
    ) ??
    getCollectionCount(requiredDocs) ??
    (uploadedCount !== null ? uploadedCount + missingCount : null) ??
    toNonNegativeNumber(fallbackRequiredCount) ??
    0;

  return { requiredCount, missingCount, uploadedCount, missingDocs };
};

export const getRequiredEmploymentDocumentCount = (user, fallback = 7) => {
  let requiredDocuments;

  switch (user?.employment_status) {
    case "student":
      requiredDocuments = constants.PROFESSION_DOCS.STUDENT_DOCS;
      break;
    case "employed":
      requiredDocuments =
        user?.employment_category === constants.domestic_worker
          ? constants.PROFESSION_DOCS.DOMESTIC_WORKER_DOCS
          : constants.PROFESSION_DOCS.EMPLOYED_DOCS;
      break;
    case "self_employed":
      if (user?.employment_category === "freelancer") {
        requiredDocuments = constants.PROFESSION_DOCS.FREELANCER_DOCS;
      } else if (user?.employment_category === "ownACompany") {
        requiredDocuments = constants.PROFESSION_DOCS.SELF_EMPLOYED_DOCS;
      } else {
        return fallback;
      }
      break;
    case "retired":
      requiredDocuments = constants.PROFESSION_DOCS.RETIRED_DOCS;
      break;
    case "unemployed":
      requiredDocuments = constants.PROFESSION_DOCS.UNEMPLOYED_DOCS;
      break;
    case "public_servant":
      requiredDocuments = constants.PROFESSION_DOCS.PUBLIC_SERVANT_DOCS;
      break;
    case constants.company_tenant:
      requiredDocuments = docsData.COMPANY_TENANT_DOCS;
      break;
    default:
      return fallback;
  }

  const documentNames = Object.keys(requiredDocuments || {});
  const hasBundledPayslips = documentNames.some((name) =>
    ["payslip", "last 3 payslips"].includes(name.toLowerCase())
  );

  return hasBundledPayslips
    ? documentNames.length + 2
    : documentNames.length || fallback;
};

const getMaximumBasePoints = (scoring) =>
  scoring.basicInformation +
  scoring.kyc +
  scoring.requiredDocuments +
  scoring.bankConnection +
  (scoring.targetRent ?? 0);

const getBasePointBreakdown = ({
  user,
  documentCounts,
  bankConnected,
  hasTargetRent = false,
  scoring,
}) => {
  const basicInformationPoints =
    user?.profile_completed === true ? scoring.basicInformation : 0;
  // #NOTE: We are not considering KYC AML here, only to tenant
  const kycState = getKycScoreState(user, null, false);
  const kycPoints = getKycPoints(user, scoring.kyc);
  const requiredDocuments = getRequiredDocumentBreakdown({
    ...documentCounts,
    maximumPoints: scoring.requiredDocuments,
  });
  const bankConnectionPoints = bankConnected ? scoring.bankConnection : 0;
  const targetRentPoints =
    hasTargetRent && scoring.targetRent ? scoring.targetRent : 0;

  const earnedPoints =
    basicInformationPoints +
    kycPoints +
    requiredDocuments.earned +
    bankConnectionPoints +
    targetRentPoints;

  return {
    earnedPoints,
    maximumPoints: getMaximumBasePoints(scoring),
    components: {
      basicInformation: {
        completed: user?.profile_completed === true,
        earned: basicInformationPoints,
        maximum: scoring.basicInformation,
      },
      kyc: {
        state: kycState,
        backendStatus: getKycStatus(user),
        earned: kycPoints,
        maximum: scoring.kyc,
      },
      requiredDocuments,
      ...(scoring.targetRent
        ? {
            targetRent: {
              persisted: hasTargetRent,
              earned: targetRentPoints,
              maximum: scoring.targetRent,
            },
          }
        : {}),
      bankConnection: {
        connected: bankConnected,
        earned: bankConnectionPoints,
        maximum: scoring.bankConnection,
      },
    },
  };
};

export const calculateGuarantorScoreBreakdown = ({
  guarantor,
  documentCounts,
  bankConnected = Boolean(
    guarantor?.bank_status && !guarantor?.financial_info_errors
  ),
}) => {
  const scoring = PROFILE_COMPLETION_SCORING.guarantor;
  const raw = getBasePointBreakdown({
    user: guarantor,
    documentCounts,
    bankConnected,
    scoring,
  });
  const completenessPercentage =
    raw.maximumPoints > 0
      ? (clamp(raw.earnedPoints, 0, raw.maximumPoints) / raw.maximumPoints) *
        100
      : 0;
  const contribution =
    (completenessPercentage / 100) * scoring.maximumContribution;

  return {
    guarantorId: guarantor?._id ?? null,
    guarantorName:
      [guarantor?.fname, guarantor?.lname].filter(Boolean).join(" ") || null,
    rawPoints: raw.earnedPoints,
    rawMaximum: raw.maximumPoints,
    completenessPercentage,
    contribution,
    maximumContribution: scoring.maximumContribution,
    components: raw.components,
  };
};

export const calculateGuarantorContribution = (input) =>
  calculateGuarantorScoreBreakdown(input).contribution;

export const calculateTenantCompletionBreakdown = ({
  user,
  documentCounts,
  bankConnected,
  hasTargetRent,
  isGuarantorRequired,
  guarantorContribution = 0,
}) => {
  const scoring = PROFILE_COMPLETION_SCORING.tenant;
  const tenant = getBasePointBreakdown({
    user,
    documentCounts,
    bankConnected,
    hasTargetRent,
    scoring,
  });
  const applicableMaximum = isGuarantorRequired
    ? tenant.maximumPoints +
      PROFILE_COMPLETION_SCORING.guarantor.maximumContribution
    : tenant.maximumPoints;
  const earnedPoints =
    tenant.earnedPoints +
    (isGuarantorRequired
      ? clamp(
          guarantorContribution,
          0,
          PROFILE_COMPLETION_SCORING.guarantor.maximumContribution
        )
      : 0);
  const percentage = Math.round(
    clamp((earnedPoints / applicableMaximum) * 100, 0, 100)
  );

  return {
    percentage,
    earnedPoints,
    applicableMaximum,
    isGuarantorRequired,
    tenant,
    guarantor: {
      earnedContribution: isGuarantorRequired
        ? clamp(
            guarantorContribution,
            0,
            PROFILE_COMPLETION_SCORING.guarantor.maximumContribution
          )
        : 0,
      maximumContribution: isGuarantorRequired
        ? PROFILE_COMPLETION_SCORING.guarantor.maximumContribution
        : 0,
    },
    formula: `${earnedPoints.toFixed(2)} / ${applicableMaximum} × 100 = ${percentage}%`,
  };
};

export const calculateTenantCompletionPercentage = (input) =>
  calculateTenantCompletionBreakdown(input).percentage;
